import type { ChangeEvent, RefObject } from "react";
import { useState, useEffect, useRef } from "react";
import { useRouter } from "next/router";
import type { SearchHistoryType } from "~/components/blocks/layout/global-navi/util/search-history-manipulator";
import {
  removeSearchHistory,
  getSearchHistories,
  appendSearchHistory,
} from "~/components/blocks/layout/global-navi/util/search-history-manipulator";
import { runningInBrowser } from "~/const/browser";
import type { ListSearchQuery } from "~/const/hrefs";
import { PATHS } from "~/const/paths";
import { useQueryString } from "~/hooks/use-query-string";
import { useRouterForVodList } from "~/hooks/use-router-for-vod-list";
import { sendTrackingClickCommon } from "~/lib/tracking/events";
import { useAuth } from "~/provider/auth";

let timerId: number;

type UseSearchFormProps = {
  /** 検索キーワード */
  keyword: string;
  /** 検索キーワード更新関数 */
  onChangeKeyword: (e: ChangeEvent<HTMLInputElement>) => void;
  /** 検索キーワード上書き関数 */
  onOverWriteKeyword: (text: string) => void;
  /** 検索フォームのRef Object */
  searchInputRef: RefObject<HTMLLabelElement>;
  /** 検索履歴リストの表示関数 */
  onVisibleSearchList: () => void;
  /** 検索履歴リストの非表示関数 */
  onHideHistoryList: () => void;
  /** 検索フォーム非表示関数 */
  onHideSearch: () => void;
};

export const useMobileSearchForm = (props: UseSearchFormProps) => {
  const {
    searchInputRef,
    keyword,
    onOverWriteKeyword,
    onHideHistoryList,
    onVisibleSearchList,
    onHideSearch,
    onChangeKeyword,
  } = props;

  const { asPath } = useRouter();
  const { pushToVodListWithoutProductModal } = useRouterForVodList();
  const query = useQueryString();
  const { isLoggedIn, userId } = useAuth();

  const [isComposingFlag, setIsComposingFlag] = useState(false);
  const [searchHistories, setSearchHistories] = useState(isLoggedIn ? getSearchHistories(userId) : []);
  const isVodGravureTop = asPath.startsWith(PATHS.VOD_GRAVURE);
  const isRunningLazySearch = useRef(false);

  /** 文字列制御 */
  const onChangeAndSearchKeyword = (e: ChangeEvent<HTMLInputElement>) => {
    isRunningLazySearch.current = true;
    const { value } = e.currentTarget;
    onChangeKeyword(e);
    if (value.trim() === "") return;
    liveSearch(value);
  };

  /** キャンセル押下時処理 */
  const onCancel = async () => {
    onHideHistoryList();
    onHideSearch();

    setTimeout(() => {
      onInitText();
    }, 150);
  };

  const onBrowserBack = async () => {
    onHideHistoryList();
    onHideSearch();
    setTimeout(() => {
      onInitText();
    }, 150);
  };

  const pushToVideoList = async ({ keyword, type }: SearchHistoryType) => {
    // eslint-disable-next-line unused-imports/no-unused-vars
    const { keyword: _, ...rest } = query;
    const isGravure = type === "gravure";
    const newQuery: ListSearchQuery =
      keyword === ""
        ? rest
        : {
            ...rest,
            keyword,
          };

    await pushToVodListWithoutProductModal({ query: newQuery, isGravure });

    isRunningLazySearch.current = false;
  };

  /** ライブ検索 */
  const liveSearch = (value: string) => {
    if (!runningInBrowser) return;
    clearTimeout(timerId);
    timerId = window.setTimeout(() => {
      const trimmedValue = value.trim();

      // MEMO（kimura）: 文字列の両端の空白を削除した上で空文字でない場合のみ検索履歴を隠す
      if (trimmedValue !== "") {
        onHideHistoryList();
      }
      sendTrackingClickCommon({ clickEventName: "dmmtv_button_search", viaInfo: "search", misc: keyword });
      // MEMO（kimura）: 空白を削除した文字列（trimmedValue）で検索は実行しない。理由は検索実行後にtrimされるから。
      pushToVideoList({ keyword: value, type: isVodGravureTop ? "gravure" : undefined });
    }, 500);
  };

  /** 検索履歴押下時キーワード更新 */
  const onUpdateKeyword = (history: string) => {
    onOverWriteKeyword(history);
  };

  /** 履歴更新 */
  const updateSearchHistoryState = () => {
    setSearchHistories(getSearchHistories(userId));
  };

  /** 履歴削除 */
  const onRemoveHistory = (index: number) => {
    removeSearchHistory(index, userId);
    updateSearchHistoryState();
  };

  /** 文字列初期化 */
  const onInitText = () => {
    onOverWriteKeyword("");
  };

  const onBlurSearchForm = () => {
    if (!isLoggedIn) return;
    appendSearchHistory({ keyword, type: asPath.includes(PATHS.VOD_GRAVURE) ? "gravure" : undefined }, userId);
    updateSearchHistoryState();
  };

  const onClickClearButton = () => {
    onInitText();
    pushToVideoList({ keyword: "", type: isVodGravureTop ? "gravure" : undefined });
  };

  const queryInputElement = (): HTMLInputElement | null => {
    const searchElement = searchInputRef.current;
    if (searchElement === null) return null;
    return searchElement.querySelector("input");
  };

  const inputIsFocused = (): boolean => {
    const inputElement = queryInputElement();
    return document.activeElement === inputElement;
  };

  const blurInputFocus = () => {
    const inputElement = queryInputElement();
    inputElement?.blur();
  };

  /** keyword === "" && IME走っていない場合に履歴リスト表示 */
  useEffect(() => {
    if (isComposingFlag) return;

    setTimeout(() => {
      if (inputIsFocused() && keyword === "") {
        onVisibleSearchList();
      }
    });
  }, [keyword]);

  /** keyword query を監視する副作用処理 */
  const queryParamsListener = () => {
    if (isRunningLazySearch.current) return;
    onOverWriteKeyword(query?.keyword ?? "");
  };
  useEffect(queryParamsListener, [query?.keyword]);

  /** Keyboard Event Hack */
  useEffect(() => {
    const searchElement = searchInputRef.current;
    if (!searchElement) return;
    const inputElement = searchElement.querySelector("input") as HTMLInputElement;

    /** Enter押下時処理 */
    const keyboardListener = (event: KeyboardEvent) => {
      // IME実行中に文字削除で初期化
      if (event.key === "Backspace") {
        setIsComposingFlag(false);
      }
      // IME実行中処理
      if (event.isComposing) return;
      if (event.key === "Enter") {
        if (inputIsFocused()) {
          blurInputFocus();
        }
      }
    };
    const compositionStart = () => setIsComposingFlag(true);
    const compositionEnd = () => setIsComposingFlag(false);

    inputElement.addEventListener("keydown", keyboardListener, {
      passive: true,
    });
    inputElement.addEventListener("compositionstart", compositionStart, {
      passive: true,
    });
    inputElement.addEventListener("compositionend", compositionEnd, {
      passive: true,
    });

    return () => {
      inputElement.removeEventListener("keydown", keyboardListener);
      inputElement.removeEventListener("compositionstart", compositionStart);
      inputElement.removeEventListener("compositionend", compositionEnd);
    };
  }, []);

  return {
    keyword,
    searchHistories,
    onRemoveHistory,
    onChangeAndSearchKeyword,
    pushToVideoList,
    onClickClearButton,
    onUpdateKeyword,
    onCancel,
    onBrowserBack,
    onBlurSearchForm,
  };
};
